import { IconNames } from '@blueprintjs/icons';
import { ActionButtonText } from '@strata/react-buttons';
import { FileUploadFormik } from '@strata/react-file-upload';
import { FormContainer } from '@strata/react-form-container';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { InlineMessage } from '@strata/react-inline-message';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { saveAs } from 'file-saver';
import { Formik } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { BulkUploadValidationErrorsDialog } from 'components/dialogs/BulkUploadValidationErrorsDialog';
import { handleError } from 'services/ErrorManagement';
import { getFile, postFile } from 'services/Http';

const UploadNotes = [
    {
        title: 'File Completion Tips',
        notes: [
            'All fields are required.',
            'Place of Service can contain a specific Place of Service or be left empty and be assigned "All Places of Service."',
            'For the Pharmacies field please enter a 10-digit NPI number.',
            'Discount amounts are AWP or ASP. For AWP - 20, the user would enter "AWP" in the Referent Type field and simply enter "-.2" for the Rate field.',
            'Start and end date MUST be formatted like so: MM/DD/YYYY',
        ],
    },
    {
        title: 'File Upload Tips',
        notes: [
            'A bulk upload file will overwrite any existing Pharmacy and Contract Rate for a unique match of Payer Segment, Drug, Benefit, and Place of Service.',
            'A post live referral assignment not included in a bulk upload will NOT be deleted or edited.',
        ],
    },
];

export const BackstopPharmaciesBulkUpload = () => {
    const navigate = useNavigate();
    const [isGeneratingFile, setIsGeneratingFile] = useState(false);
    const generateFile = async () => {
        try {
            setIsGeneratingFile(true);
            saveAs(
                await getFile('/assignment/backstop_pharmacies/bulk_upload/template'),
                'Post Live Referral Assignment Upload.xlsx',
            );
        } catch (err) {
            handleError('An error occurred generating file. Please try again.', err);
        } finally {
            setIsGeneratingFile(false);
        }
    };

    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);

    return (
        <Formik
            initialValues={{}}
            onSubmit={async (values, { setFieldValue, resetForm }) => {
                try {
                    const validationErrors = await postFile(
                        '/assignment/backstop_pharmacies/bulk_upload',
                        values.fileToUpload,
                    );

                    if (validationErrors?.length > 0) {
                        handleError('File not uploaded. Invalid values found in file.');
                        await setFieldValue('validationErrors', validationErrors);
                    } else {
                        SuccessToast({ message: 'File successfully uploaded.' });
                        resetForm();
                        navigate('/route-management/post-live-referral-assignment');
                    }
                } catch (err) {
                    handleError('An error occurred uploading file. Please try again.', err);
                }
            }}
            validationSchema={Yup.object({
                backstopPharmaciesFile: Yup.array()
                    .required(validationStrings.requiredField.errorMessage)
                    .length(1, validationStrings.requiredField.errorMessage),
            })}
        >
            {(formik) => (
                <FormContainer
                    formTitle="Bulk Upload Post Live Referral Assignments"
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !formik.isValid || !formik.dirty}
                    isLoading={formik.isSubmitting}
                    formHeaderRightContent={
                        <>
                            <FullscreenLoading
                                isShowing={isGeneratingFile}
                                loadingText="Generating File..."
                            />
                            <ActionButtonText
                                label="Generate File"
                                leftIcon={IconNames.IMPORT}
                                onClick={generateFile}
                            />
                        </>
                    }
                    formLeftContent={
                        <>
                            <BulkUploadValidationErrorsDialog
                                uploadName="Post Live Referral Assignment"
                                isOpen={showValidationErrorDialog}
                                onClose={() => setShowValidationErrorDialog(false)}
                                validationErrors={formik.values.validationErrors}
                            />
                            <FileUploadFormik
                                id="backstopPharmaciesFile"
                                label="Post Live Referral Assignment File"
                                required
                                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                maxAllowedFiles={1}
                                onAddFiles={(files) =>
                                    formik.setFieldValue('fileToUpload', files?.[0])
                                }
                                onRemoveFile={() => formik.setFieldValue('fileToUpload', undefined)}
                            />
                            {formik.values.validationErrors && (
                                <InlineMessage
                                    intent={InlineMessage.Intent.ERROR}
                                    wrapText
                                    label={
                                        <>
                                            <span>
                                                An error occurred while uploading the post live
                                                referral assignment file.
                                            </span>
                                            <br />
                                            <span>
                                                Please fix the errors in the uploaded file.&nbsp;
                                            </span>
                                            <u
                                                style={{ cursor: 'pointer' }}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => setShowValidationErrorDialog(true)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        setShowValidationErrorDialog(true);
                                                    }
                                                }}
                                            >
                                                View Details
                                            </u>
                                        </>
                                    }
                                />
                            )}
                        </>
                    }
                    formRightContent={UploadNotes.map((note) => (
                        <div key={note.title} className="sds_bulk-upload__notes">
                            <InlineMessage
                                intent={InlineMessage.Intent.TIP}
                                title={note.title}
                                wrapText
                                label={
                                    <ul>
                                        {note.notes.map((bullet) => (
                                            <li key={bullet}>{bullet}</li>
                                        ))}
                                    </ul>
                                }
                            />
                        </div>
                    ))}
                />
            )}
        </Formik>
    );
};
